import type { BookingCreateCarRecord, BookingProposalItemRecord } from '@navio/nsdk';
import { PaymentOption, StuddedTireSeasonOverrideReason } from '@navio/nsdk';
import { noddiAsync, URLKeys } from 'noddi-async';
import type { BookingUserNotificationDto, SelectedSalesItemsCarGrouped } from 'noddi-types';
import { customerRoutes } from 'noddi-util';
import {
  commonTrackingEvents,
  getStuddedTireSeasonExplanation,
  tracking,
  withStuddedTireSeasonOverride
} from 'noddi-web';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useCustomerDepartmentSelection } from '../../../hooks/useCustomerDepartmentSelection';
import {
  useBookingProposalActions,
  useBookingProposalAddress,
  useBookingProposalAnalytics,
  useBookingProposalComment,
  useBookingProposalContactUserId,
  useBookingProposalId,
  useBookingProposalItems,
  useBookingProposalPinLocation,
  useBookingProposalTimeWindow,
  useBookingProposalUserGroupId
} from '../../../stores/bookingProposalStore';
import { useCurrentBrand } from '../../../stores/BrandStore/brandStore';
import { routePaymentSession } from '../../../utils/paymentSession';
import { setProposalPayBeforeCheckoutInFlight } from '../../../utils/proposalPayBeforeCheckout';

interface Props {
  onMutate?: () => void;
  paymentMethod?: PaymentOption;
  /** Invoice recipient when it differs from the customer. Omitted when the customer pays. */
  userGroupPayerId?: number | null;
}

export const useCreateBookingFromProposal = ({ onMutate, paymentMethod, userGroupPayerId }: Props) => {
  const navigate = useNavigate();
  const userGroupId = useBookingProposalUserGroupId();
  const { getSelectedDepartmentForGroup } = useCustomerDepartmentSelection();
  const bookedForUserId = useBookingProposalContactUserId();
  const address = useBookingProposalAddress();
  const selectedBookingTimeWindow = useBookingProposalTimeWindow();
  const bookingProposalItems = useBookingProposalItems();
  const bookingProposalId = useBookingProposalId();
  const analytics = useBookingProposalAnalytics();
  const commentsUser = useBookingProposalComment();
  const { clearStore } = useBookingProposalActions();
  const { currentBrand } = useCurrentBrand();

  const pinLocation = useBookingProposalPinLocation();
  // Set before the draft is cleared and kept whatever follows: a failed payment session after a created booking
  // leaves both mutations in error, and the page must still show that error rather than a lost draft.
  const [hasCreatedBooking, setHasCreatedBooking] = useState(false);

  const trackProposalAccepted = (bookingId?: number) => {
    tracking.track(commonTrackingEvents.proposalAccepted, { ...analytics, bookingId });
  };

  const {
    mutateAsync: createPaymentSession,
    isPending: isCreatingPaymentSession,
    error: paymentSessionError
  } = noddiAsync.usePost({
    type: URLKeys.postOrderPaymentSession,
    queryConfig: {
      onSuccess: ({ data: sessionData }) => {
        routePaymentSession({
          session: sessionData,
          // oxlint-disable-next-line typescript/no-misused-promises -- routePaymentSession takes navigate typed () => void; react-router's navigate returns a promise that only tracks lazy-route loading
          navigate,
          getEmbeddedPath: (session) =>
            customerRoutes.payForBooking.getPath({
              sessionId: session.sessionId,
              countryCode: session.countryCode
            }),
          navigateOptions: { replace: true }
        });
      }
    }
  });

  const {
    mutateAsync: createBookingPayBefore,
    isPending: isPayBeforePending,
    error: payBeforeError,
    reset: resetPayBefore
  } = noddiAsync.usePost({
    type: URLKeys.postCreateBookingIgnoringImpersonation,
    queryConfig: {
      onMutate: () => {
        setProposalPayBeforeCheckoutInFlight(true);
      },
      onSuccess: async (response) => {
        trackProposalAccepted(response.data.id);
        setHasCreatedBooking(true);
        clearStore();
        const order = response.data.order!;
        try {
          if (order.amountGross.amount > 0) {
            await createPaymentSession({ orderId: order.id });
          } else {
            navigate(customerRoutes.confirmation.getPath({ slug: response.data.slug }), { replace: true });
          }
        } finally {
          queueMicrotask(() => setProposalPayBeforeCheckoutInFlight(false));
        }
      },
      onError: () => {
        setProposalPayBeforeCheckoutInFlight(false);
      }
    }
  });

  const {
    mutateAsync: createBookingPayAfter,
    isPending: isPayAfterPending,
    error: payAfterError
  } = noddiAsync.usePost({
    type: URLKeys.postCreateBookingIgnoringImpersonation,
    queryConfig: {
      onMutate,
      onSuccess: (response) => {
        trackProposalAccepted(response.data.id);
        setHasCreatedBooking(true);
        clearStore();
      },
      mutationKey: [URLKeys.postCreateBookingIgnoringImpersonation, bookingProposalId]
    }
  });

  const mapBookingProposalItemsToSelectedSalesItemsCars = (
    bookingProposalItems: BookingProposalItemRecord[]
  ): SelectedSalesItemsCarGrouped[] => {
    return bookingProposalItems.map((bookingProposalItem) => ({
      licensePlate: bookingProposalItem.car.licensePlate,
      selectedSalesItemIds: bookingProposalItem.salesItemsWithQuantity.map((salesItem) => salesItem.salesItem.id)
    }));
  };

  // Reused when the customer confirms the studded-tire season prompt, so the notification choices made before the
  // refusal survive the second post.
  const [lastNotificationsToOverride, setLastNotificationsToOverride] = useState<BookingUserNotificationDto[]>([]);

  const onSubmit = async (
    notificationsToOverride: BookingUserNotificationDto[] = [],
    studdedTireSeasonOverrideReason?: StuddedTireSeasonOverrideReason
  ) => {
    setLastNotificationsToOverride(notificationsToOverride);
    const payload = {
      cars: mapBookingProposalItemsToSelectedSalesItemsCars(bookingProposalItems) as BookingCreateCarRecord[],
      addressId: address!.id,
      deliveryWindow: {
        id: selectedBookingTimeWindow!.deliveryWindowId,
        endsAt: selectedBookingTimeWindow!.endsAt,
        startsAt: selectedBookingTimeWindow!.startsAt
      },
      userId: bookedForUserId,
      userGroupId,
      customerDepartmentId: getSelectedDepartmentForGroup(userGroupId)?.id,
      commentsUser: commentsUser ?? '',
      notificationsToOverride,
      bookingProposalId,
      brandDomain: currentBrand,
      pinLocationLongitude: pinLocation?.lng,
      pinLocationLatitude: pinLocation?.lat,
      payment: paymentMethod ? { paymentMethod } : undefined,
      ...(userGroupPayerId != null ? { userGroupPayerId } : {})
    };
    const body = studdedTireSeasonOverrideReason
      ? withStuddedTireSeasonOverride(payload, studdedTireSeasonOverrideReason)
      : payload;

    if (paymentMethod === PaymentOption.PAY_BEFORE) {
      await createBookingPayBefore(body);
    } else {
      await createBookingPayAfter(body);
    }
  };

  /**
   * Dialog props when the proposal was refused for an out-of-season studded-tire date, else null. Confirming posts
   * the same proposal again with an is_studded approval on each car.
   */
  const getStuddedTireSeasonPrompt = (error: unknown, onDismiss: () => void) => {
    if (getStuddedTireSeasonExplanation(error) === null) {
      return null;
    }
    return {
      onConfirm: () => {
        // oxlint-disable-next-line typescript/no-floating-promises -- failures surface through the mutation state
        onSubmit(lastNotificationsToOverride, StuddedTireSeasonOverrideReason.CUSTOMER_REQUEST);
      },
      onDismiss
    };
  };

  const error = payBeforeError ?? payAfterError ?? paymentSessionError;

  return {
    createProposalBooking: onSubmit,
    getStuddedTireSeasonPrompt,
    /** Pay-before refusals land on the confirmation page; pay-after ones in the builder, which reads its own mutation. */
    studdedTireSeason: getStuddedTireSeasonPrompt(payBeforeError, resetPayBefore),
    isPending: isPayBeforePending || isPayAfterPending || isCreatingPaymentSession,
    /** A booking is being or was posted from this mount. Its success clears the draft, which must not read as a lost draft. */
    hasCreatedBooking: hasCreatedBooking || isPayBeforePending || isPayAfterPending,
    // The studded-tire refusal has its own dialog; rendering it as an error too would repeat the rule behind it.
    error: getStuddedTireSeasonExplanation(error) ? null : error
  };
};
